import { useTheme } from "@/components/context/ThemeContext";
import { useState, useEffect } from "react";
import styles from "./vip-tiers.module.css";
import VipPurchaseSection from "./VipPurchaseSection";

const VipTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredTier, setHoveredTier] = useState(null);

  const { theme } = useTheme();

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const API_BASE_URL =
          import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1";
        const response = await fetch(`${API_BASE_URL}/vip/tiers`);
        if (!response.ok) throw new Error("Failed to fetch tiers");
        const data = await response.json();
        if (data.success) {
          setTiers(data.tiers.sort((a, b) => a.sortOrder - b.sortOrder));
          console.log(data.tiers);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  if (loading)
    return (
      <div className={styles.cosmicBackground}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              className={styles.cosmicLoader}
              style={{ marginBottom: "1rem" }}
            ></div>
            <p
              style={{
                color: "white",
                fontSize: "1.25rem",
                fontWeight: 300,
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            >
              Loading Cosmic Tiers...
            </p>
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div
        style={{
          background:
            "radial-gradient(ellipse at top, #1a1a2e 0%, #16213e 40%, #0f0f23 100%)",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", color: "#fca5a5" }}>
          <div style={{ fontSize: "3.75rem", marginBottom: "1rem" }}>⚠️</div>
          <p style={{ fontSize: "1.25rem", fontWeight: 300 }}>Error: {error}</p>
        </div>
      </div>
    );

  const getTierConfig = (tier) => {
    switch (tier.key) {
      case "VIP1":
        return {
          title: "Lunar Initiate",
          subtitle: "Your journey begins under lunar light.",
          icon: "✨",
          theme: "lunar",
          cardClass: styles.lunarCard,
          ringClass: styles.lunarRing,
          badgeClass: styles.lunarBadge,
          buttonClass: styles.lunarButton,
          textGlow: styles.lunarTextGlow,
          particles: "lunar",
        };
      case "VIP2":
        return {
          title: "Pixel Guardian",
          subtitle: "You no longer play among stars — you command them.",
          icon: "🌌",
          theme: "guardian",
          cardClass: styles.guardianCard,
          ringClass: styles.guardianRing,
          badgeClass: styles.guardianBadge,
          buttonClass: styles.guardianButton,
          textGlow: styles.guardianTextGlow,
          particles: "guardian",
        };
      case "VIP3":
        return {
          title: "Celestial Overlord",
          subtitle: "Welcome to the throne above galaxies.",
          icon: "🛡️",
          theme: "overlord",
          cardClass: styles.overlordCard,
          ringClass: styles.overlordRing,
          badgeClass: styles.overlordBadge,
          buttonClass: styles.overlordButton,
          textGlow: styles.overlordTextGlow,
          particles: "overlord",
        };
      case "12":
        return {
          title: tier.name,
          subtitle: "Entry-level VIP with gtrg perks",
          icon: "🌟",
          theme: "basic",
          cardClass: styles.basicCard,
          ringClass: styles.lunarRing,
          badgeClass: styles.lunarBadge,
          buttonClass: styles.lunarButton,
          textGlow: styles.basicTextGlow,
          particles: "lunar",
        };
      case "GLOD":
        return {
          title: tier.name,
          subtitle: "Entry-level VIP with Maxi Calypse perks",
          icon: "🌟",
          theme: "basic",
          cardClass: styles.basicCard,
          ringClass: styles.lunarRing,
          badgeClass: styles.lunarBadge,
          buttonClass: styles.lunarButton,
          textGlow: styles.basicTextGlow,
          particles: "lunar",
        };
      default:
        return {
          title: "Basic VIP",
          subtitle: "Entry-level VIP with basic perks",
          icon: "🌟",
          theme: "basic",
          cardClass: styles.basicCard,
          ringClass: styles.lunarRing,
          badgeClass: styles.lunarBadge,
          buttonClass: styles.lunarButton,
          textGlow: styles.basicTextGlow,
          particles: "lunar",
        };
    }
  };

  const ParticleField = ({ theme, isActive }) => (
    <div
      className={`${styles.particleField} ${isActive ? styles.particleFieldActive : styles.particleFieldInactive}`}
    >
      {[
        ...Array(theme === "overlord" ? 20 : theme === "guardian" ? 15 : 10),
      ].map((_, i) => {
        const particleClass =
          theme === "overlord"
            ? styles.particleOverlord
            : theme === "guardian"
              ? styles.particleGuardian
              : styles.particleLunar;
        return (
          <div
            key={i}
            className={`${styles.particle} ${particleClass}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        );
      })}
    </div>
  );

  return (
    <div
      className={
        theme === "dark"
          ? styles.cosmicBackground
          : styles.cosmicBackgroundLight
      }
      style={{ position: "relative", overflow: "hidden" }}
    >
      {/* Cosmic Background Elements */}
      <div className={styles.starsBackground}></div>
      <div className={styles.nebulaOverlay}></div>

      <div className={styles.container}>
        {/* Cosmic Header */}
        <div className={styles.headerSection}>
          <h1 className={styles.cosmicTitle}>
            <span className={styles.cosmicGradientText}>VIP COSMIC TIERS</span>
          </h1>
          <p className={styles.headerText}>
            Ascend through the cosmic hierarchy and unlock legendary powers
            beyond imagination
          </p>
          <div className={styles.cosmicDivider}></div>
        </div>

        {/* Tiers Grid */}
        <div className={styles.tiersGrid}>
          {tiers.map((tier, index) => {
            const config = getTierConfig(tier);
            const isHovered = hoveredTier === tier._id;

            // Add dark or light class from CSS module to theme the card directly
            const themeClass = theme === "dark" ? styles.dark : styles.light;

            return (
              <div
                key={tier._id}
                className={`${styles.tierCard} ${config.cardClass} ${themeClass} ${isHovered ? styles.tierCardActive : ""}`}
                onMouseEnter={() => setHoveredTier(tier._id)}
                onMouseLeave={() => setHoveredTier(null)}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Particle Effects */}
                <ParticleField theme={config.theme} isActive={isHovered} />

                {/* Tier Header */}
                <div
                  style={{
                    position: "relative",
                    zIndex: 10,
                    textAlign: "center",
                    marginBottom: "2.5rem",
                  }}
                >
                  {/* Avatar Ring */}
                  <div
                    className={`${styles.tierAvatar} ${config.ringClass} ${isHovered ? styles.tierAvatarActive : ""}`}
                  >
                    {config.ringClass === styles.guardianRing && (
                      <div className={styles.guardedRingInner}></div>
                    )}
                    <div className={styles.tierIcon}>{config.icon}</div>
                    <div className={styles.tierRingGlow}></div>
                  </div>

                  {/* Badge */}
                  <div className={`${styles.tierBadge} ${config.badgeClass}`}>
                    {tier.badgeText || config.title}
                  </div>
                </div>

                {/* Tier Info */}
                <div
                  style={{
                    position: "relative",
                    zIndex: 10,
                    textAlign: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <h2
                    className={`${styles.tierTitle} ${config.textGlow} ${themeClass}`}
                  >
                    {config.title}
                  </h2>
                  <p className={`${styles.tierSubtitle} ${themeClass}`}>
                    {config.subtitle}
                  </p>
                </div>

                <VipPurchaseSection tier={tier} variant="compact" />

                {/* Stats Grid */}
                <div
                  className={styles.tierStats}
                  style={{ marginBottom: "2.5rem" }}
                >
                  <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>
                        {tier.unlockOrders}
                      </div>
                      <div className={styles.statLabel}>Orders</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>
                        {tier.unlockWindowDays}
                      </div>
                      <div className={styles.statLabel}>Window Days</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>
                        {tier.freeValidityDays}
                      </div>
                      <div className={styles.statLabel}>Free Days</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>
                        ₹{tier.paidPriceInr}
                      </div>
                      <div className={styles.statLabel}>Price</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>
                        {tier.discountPercent}%
                      </div>
                      <div className={styles.statLabel}>Discount</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>{tier.multiplier}x</div>
                      <div className={styles.statLabel}>Multiplier</div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  className={`${styles.tierCta} ${config.buttonClass} ${themeClass}`}
                >
                  <span>Ascend to {config.title}</span>
                  <div className={styles.buttonGlow}></div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className={styles.bottomSection}>
          <div
            className={styles.cosmicDivider}
            style={{ marginBottom: "2rem" }}
          ></div>
          <p className={styles.bottomText}>
            Choose your destiny among the stars
          </p>
        </div>
      </div>
    </div>
  );
};

export default VipTiers;
